import React, { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useI18n } from '../i18n/I18nProvider';

/**
 * Compact language dropdown for the header.
 * Shows current flag + code; opens a menu with all supported languages.
 * Persists selection to localStorage via I18nProvider.
 */
export const LanguageSwitcher = ({ variant = 'default' }) => {
  const { lang, setLang, languages, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const current = languages.find((l) => l.code === lang) || languages[0];

  const isCompact = variant === 'compact';

  return (
    <div className="relative" ref={ref} data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('lang.switch_language')}
        aria-expanded={open}
        className={
          isCompact
            ? 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/80 hover:bg-white border border-gray-200 text-xs font-semibold text-[#0A1628] transition-colors'
            : 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white hover:bg-gray-50 border border-gray-200 text-sm font-semibold text-[#0A1628] transition-colors shadow-sm'
        }
        data-testid="language-switcher-btn"
      >
        <Globe className="w-3.5 h-3.5 text-[#1565C0]" />
        <span className="text-base leading-none">{current.flag}</span>
        <span className="hidden sm:inline">{current.code.toUpperCase()}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2"
          data-testid="language-switcher-menu"
        >
          <div className="px-3 pt-1 pb-2 text-[10px] font-black uppercase tracking-widest text-gray-400 border-b border-gray-100">
            {t('lang.switch_language')}
          </div>
          {languages.map((l) => {
            const active = l.code === lang;
            return (
              <button
                key={l.code}
                type="button"
                onClick={() => { setLang(l.code); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left text-sm hover:bg-amber-50 transition-colors ${active ? 'bg-amber-50/60 font-semibold' : ''}`}
                data-testid={`language-option-${l.code}`}
              >
                <span className="text-xl leading-none">{l.flag}</span>
                <span className="flex-1">
                  <span className="block text-[#0A1628]">{l.native}</span>
                  <span className="block text-[10px] uppercase tracking-widest text-gray-400">{l.label}</span>
                </span>
                {active && <Check className="w-4 h-4 text-[#D4AF37]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
